import type { PostSearchItem } from '@ps/api/types';
import {
  CommunityChannelMessageLinkType,
  type CommunityChannelMessagePostLink,
} from '@ps/models/lib/community-channel';

import { getPostMediaCounts } from '../../../posts/functions/getPostMediaCounts';

/**
 * Snapshot a picked `PostSearchItem` into the canonical
 * {@link CommunityChannelMessagePostLink} that lives on `message.content.links[]`.
 * Carries enough for the receiving bubble to render the rich (and correctly
 * gated) card immediately — cover, counts, title, description, gate config — so
 * there's no follow-up-fetch height jump.
 *
 * `PostSearchItem` is API-typed; the link is model-typed (`@ps/models`). The
 * postType/media/visibility/gate blobs are structurally identical across the two
 * type systems, so they're bridged with single boundary casts, not field probes.
 */
export function postSearchItemToMessageLink(
  post: PostSearchItem,
): CommunityChannelMessagePostLink {
  const { imageCount, videoCount } = getPostMediaCounts(post.medias);
  return {
    type: CommunityChannelMessageLinkType.Post,
    id: post.id,
    cid: post.cid,
    href: post.permaLink ?? '',
    permalink: post.permaLink ?? '',
    title: post.title,
    description: post.description,
    postType:
      post.postType as unknown as CommunityChannelMessagePostLink['postType'],
    visibility:
      post.visibility as unknown as CommunityChannelMessagePostLink['visibility'],
    allowComments: post.allowComments ?? false,
    medias: post.medias as unknown as CommunityChannelMessagePostLink['medias'],
    imageCount,
    videoCount,
    publishedAt: post.publishedAt,
    createdAt: post.createdAt,
    // Gate snapshot — lets the receiving bubble render the locked layout
    // immediately (no post-fetch height jump).
    subscribeToView:
      post.subscribeToView as unknown as CommunityChannelMessagePostLink['subscribeToView'],
    payToView:
      post.payToView as unknown as CommunityChannelMessagePostLink['payToView'],
    crowdTippingToView:
      post.crowdTippingToView as unknown as CommunityChannelMessagePostLink['crowdTippingToView'],
  };
}
